import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Users, BookOpen, FileText, TrendingUp, AlertTriangle, Plus, BarChart3, Brain, 
  DollarSign, MessageSquare, Activity, CreditCard, Target, Clock, CheckCircle2
} from "lucide-react";
import { Link } from "react-router-dom";
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  BarChart, Bar, PieChart, Pie, Cell 
} from "recharts";
import { format, subDays } from "date-fns";

const CHART_COLORS = [
  "hsl(var(--primary))", 
  "hsl(var(--accent))", 
  "#f59e0b", 
  "#ef4444", 
  "#8b5cf6"
];

export const AdminOverview = () => {
  // KPI stats
  const { data: kpis } = useQuery({
    queryKey: ["admin-kpis"],
    queryFn: async () => {
      const today = new Date().toISOString().split("T")[0];
      const [mcqs, users, attempts, todayAttempts, subscribers, feedback] = await Promise.all([
        supabase.from("mcqs").select("*", { count: "exact", head: true }),
        supabase.from("profiles").select("*", { count: "exact", head: true }),
        supabase.from("attempts").select("*", { count: "exact", head: true }),
        supabase.from("attempts").select("*", { count: "exact", head: true }).gte("attempted_at", today),
        supabase.from("user_exam_access").select("*", { count: "exact", head: true }).gte("expires_at", new Date().toISOString()),
        supabase.from("mcq_user_feedback").select("*", { count: "exact", head: true }).eq("status", "pending"),
      ]);

      // Calculate pass rate from recent attempts
      const { data: recentAttempts } = await supabase
        .from("attempts")
        .select("is_correct")
        .gte("attempted_at", subDays(new Date(), 30).toISOString())
        .limit(1000);
      
      const correct = recentAttempts?.filter(a => a.is_correct).length || 0;
      const total = recentAttempts?.length || 1;
      const passRate = Math.round((correct / total) * 100);

      return {
        totalMCQs: mcqs.count || 0,
        totalUsers: users.count || 0,
        totalAttempts: attempts.count || 0,
        todayAttempts: todayAttempts.count || 0,
        activeSubscribers: subscribers.count || 0,
        pendingFeedback: feedback.count || 0,
        passRate,
      };
    },
  });

  // User signups over time (last 14 days)
  const { data: signupData = [] } = useQuery({
    queryKey: ["admin-signups-chart"],
    queryFn: async () => {
      const days = Array.from({ length: 14 }, (_, i) => subDays(new Date(), 13 - i));
      const results = await Promise.all(
        days.map(async (day) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const nextStr = format(subDays(day, -1), "yyyy-MM-dd");
          const { count } = await supabase
            .from("profiles")
            .select("*", { count: "exact", head: true })
            .gte("created_at", dateStr)
            .lt("created_at", nextStr);
          return { date: format(day, "MMM dd"), signups: count || 0 };
        })
      );
      return results;
    },
  });

  // Questions attempted per day (last 14 days)
  const { data: attemptData = [] } = useQuery({
    queryKey: ["admin-attempts-chart"],
    queryFn: async () => {
      const days = Array.from({ length: 14 }, (_, i) => subDays(new Date(), 13 - i));
      const results = await Promise.all(
        days.map(async (day) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const nextStr = format(subDays(day, -1), "yyyy-MM-dd");
          const { count } = await supabase
            .from("attempts")
            .select("*", { count: "exact", head: true })
            .gte("attempted_at", dateStr)
            .lt("attempted_at", nextStr);
          return { date: format(day, "MMM dd"), attempts: count || 0 };
        })
      );
      return results;
    },
  });

  // Subscription breakdown by type
  const { data: subscriptionBreakdown = [] } = useQuery({
    queryKey: ["admin-sub-breakdown"],
    queryFn: async () => {
      const { data } = await supabase
        .from("user_exam_access")
        .select("subscription_type")
        .gte("expires_at", new Date().toISOString());
      
      const counts: Record<string, number> = {};
      data?.forEach(row => {
        const type = row.subscription_type || "trial";
        counts[type] = (counts[type] || 0) + 1;
      });
      return Object.entries(counts).map(([name, value]) => ({ name, value }));
    },
  });

  // Recent activity
  const { data: recentActivity = [] } = useQuery({
    queryKey: ["admin-recent-activity"],
    queryFn: async () => {
      const [recentSignups, recentFeedback] = await Promise.all([
        supabase.from("profiles").select("id, full_name, created_at").order("created_at", { ascending: false }).limit(5),
        supabase.from("mcq_user_feedback").select("id, feedback_type, status, created_at").order("created_at", { ascending: false }).limit(5),
      ]);

      const items: { type: string; text: string; time: string }[] = [];
      recentSignups.data?.forEach(u => items.push({
        type: "signup",
        text: `${u.full_name || "New user"} signed up`,
        time: u.created_at,
      }));
      recentFeedback.data?.forEach(f => items.push({
        type: "feedback",
        text: `New ${f.feedback_type} feedback (${f.status})`,
        time: f.created_at,
      }));
      return items.sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()).slice(0, 8);
    },
  });

  // Content health
  const { data: contentHealth } = useQuery({
    queryKey: ["admin-content-health"],
    queryFn: async () => {
      const [noExplanation, noTags] = await Promise.all([
        supabase.from("mcqs").select("*", { count: "exact", head: true }).is("explanation", null),
        supabase.from("mcqs").select("*", { count: "exact", head: true }).or("tags.is.null,tags.eq.{}"),
      ]);
      return {
        noExplanation: noExplanation.count || 0,
        noTags: noTags.count || 0,
      };
    },
  });

  const stats = kpis || { totalMCQs: 0, totalUsers: 0, totalAttempts: 0, todayAttempts: 0, activeSubscribers: 0, pendingFeedback: 0, passRate: 0 };
  const health = contentHealth || { noExplanation: 0, noTags: 0 };

  const kpiCards = [
    { title: "Total Users", value: stats.totalUsers.toLocaleString(), icon: Users, color: "text-blue-500" },
    { title: "Active Subscribers", value: stats.activeSubscribers.toLocaleString(), icon: CreditCard, color: "text-emerald-500" },
    { title: "Total MCQs", value: stats.totalMCQs.toLocaleString(), icon: BookOpen, color: "text-violet-500" },
    { title: "Questions Today", value: stats.todayAttempts.toLocaleString(), icon: Activity, color: "text-amber-500" },
    { title: "Pass Rate (30d)", value: `${stats.passRate}%`, icon: Target, color: "text-primary" },
    { title: "Pending Feedback", value: stats.pendingFeedback.toString(), icon: MessageSquare, color: stats.pendingFeedback > 0 ? "text-destructive" : "text-muted-foreground" },
  ];

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid gap-4 grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {kpiCards.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <Card key={kpi.title} className="relative overflow-hidden">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{kpi.title}</span>
                  <Icon className={`h-4 w-4 ${kpi.color}`} />
                </div>
                <div className="text-2xl font-bold">{kpi.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* User Signups */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">User Signups</CardTitle>
            <CardDescription>New registrations over the last 14 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={signupData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} />
                  <Line type="monotone" dataKey="signups" stroke="hsl(var(--primary))" strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Questions Attempted */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Questions Attempted</CardTitle>
            <CardDescription>Daily question attempts over 14 days</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={attemptData}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: "8px", fontSize: "12px" }} />
                  <Bar dataKey="attempts" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Second charts row */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Subscription Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Subscription Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[220px]">
              {subscriptionBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={subscriptionBreakdown} cx="50%" cy="50%" innerRadius={50} outerRadius={80} paddingAngle={4} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                      {subscriptionBreakdown.map((_, index) => (
                        <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-sm text-muted-foreground">No active subscriptions</div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Content Health */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Content Health
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Missing Explanations</span>
              <Badge variant={health.noExplanation > 0 ? "destructive" : "secondary"}>{health.noExplanation}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Untagged MCQs</span>
              <Badge variant={health.noTags > 0 ? "destructive" : "secondary"}>{health.noTags}</Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Attempts</span>
              <Badge variant="outline">{stats.totalAttempts.toLocaleString()}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[220px] overflow-y-auto">
              {recentActivity.length === 0 ? (
                <p className="text-sm text-muted-foreground">No recent activity</p>
              ) : (
                recentActivity.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <div className={`mt-1 h-2 w-2 rounded-full shrink-0 ${item.type === "signup" ? "bg-emerald-500" : "bg-amber-500"}`} />
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{item.text}</p>
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(item.time), "MMM dd, HH:mm")}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            Quick Actions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            {[
              { label: "Add MCQ", icon: Plus, href: "/admin/mcqs" },
              { label: "View Users", icon: Users, href: "/admin/users" },
              { label: "AI Tools", icon: Brain, href: "/admin/ai" },
              { label: "Feedback", icon: MessageSquare, href: "/admin/feedback" },
              { label: "Analytics", icon: BarChart3, href: "/admin/analytics" },
            ].map((action) => {
              const Icon = action.icon;
              return (
                <Button key={action.label} asChild variant="outline" className="h-auto p-3 justify-start">
                  <Link to={action.href}>
                    <Icon className="h-4 w-4 mr-2" />
                    {action.label}
                  </Link>
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
